// site-create.component.ts (nur relevante Änderungen gezeigt)
import {AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, ViewChild} from "@angular/core";
import {FormBuilder, Validators} from "@angular/forms";
import {Router} from "@angular/router";
import {ModalController} from "@ionic/angular";
import * as L from "leaflet";
import {Api} from "../../generated/api/hydrosolid-portal/api";
import {siteControllerCreate} from "../../generated/api/hydrosolid-portal/functions";
import {CreateSiteDto} from "../../generated/api/hydrosolid-portal/models/create-site-dto";
import {TimezoneSelectComponent} from "../../shared/components/timezone-select/timezone-select.component";
import {Service} from "../../shared/service/service";
import {SiteCreateSuccessComponent} from "./site-create-success.component";


@Component({
  selector: "app-site-create",
  templateUrl: "./site-create.component.html",
  styleUrls: ["site-create-component.scss"],
  standalone: false,
})
export class SiteCreateComponent implements AfterViewInit {
  @ViewChild("map", {static: false}) mapEl!: ElementRef<HTMLDivElement>;

  private map?: L.Map;
  private marker?: L.Marker;

  public loading = false;

  public form = this.fb.group({
    name: ["", [Validators.required, Validators.minLength(3)]],
    lat: ["", [Validators.required, Validators.pattern(/^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?$)/)]],
    lon: ["", [Validators.required, Validators.pattern(/^[-+]?((1[0-7]\d)|([1-9]?\d))(\.\d+)?$|^[-+]?180(\.0+)?$/)]],
    timezone: ["", [Validators.required, Validators.minLength(3)]],
    setupPassword: ["", [Validators.required, Validators.minLength(6)]],
  });

  constructor(
    private fb: FormBuilder,
    private api: Api,
    private router: Router,
    public service: Service,
    private modalCtrl: ModalController,
  ) {
  }

  ngAfterViewInit(): void {
    // Warten, bis das DOM und die Größe stehen (Ionic Transition)
    setTimeout(() => this.initMap(), 0);
  }

  private initMap() {
    if (this.map || !this.mapEl) {return;}

    // Marker-Icons fixen (siehe vorherige Lösung)
    // @ts-ignore
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: "assets/leaflet/marker-icon-2x.png",
      iconUrl: "assets/leaflet/marker-icon.png",
      shadowUrl: "assets/leaflet/marker-shadow.png",
    });

    // 🌍 Zentrum & Zoom auf Europa
    const europeCenter: L.LatLngExpression = [54.5260, 15.2551]; // Mitte Europas
    const zoomLevel = 4; // Guter Start-Zoom für ganz Europa

    this.map = L.map(this.mapEl.nativeElement, {
      center: europeCenter,
      zoom: zoomLevel,
      zoomControl: true,
      attributionControl: true,
    });

    // Standard-Tiles von OpenStreetMap
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(this.map);

    // Klick auf Karte → Marker setzen & Form aktualisieren
    this.map.on("click", (e: L.LeafletMouseEvent) => {
      const {lat, lng} = e.latlng;
      this.placeOrMoveMarker([lat, lng]);
      this.form.controls.lat.setValue(this.round(lat, 6).toString());
      this.form.controls.lon.setValue(this.round(lng, 6).toString());
    });

    // Resize-Fix
    setTimeout(() => this.map?.invalidateSize(), 200);
  }

  private placeOrMoveMarker(latlng: [number, number]) {
    if (!this.map) {
      return;
    }
    if (!this.marker) {
      this.marker = L.marker(latlng, {draggable: true}).addTo(this.map);
      this.marker.on("moveend", (e: any) => {
        const pos = e.target.getLatLng();
        this.form.controls.lat.setValue(this.round(pos.lat, 6).toString());
        this.form.controls.lon.setValue(this.round(pos.lng, 6).toString());
        this.form.controls.lat.markAsDirty();
        this.form.controls.lon.markAsDirty();
      });
    } else {
      this.marker.setLatLng(latlng);
    }
    this.map.setView(latlng, Math.max(this.map.getZoom(), 8));
  }

  private round(n: number, digits = 6) {
    const p = Math.pow(10, digits);
    return Math.round(n * p) / p;
  }

  private coerceNumber(v: any): number | null {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  async openTimezoneModal() {
    const modal = await this.modalCtrl.create({
      component: TimezoneSelectComponent,
    });
    await modal.present();

    const {data, role} = await modal.onDidDismiss();
    if (role === "confirm" && data) {
      this.form.controls.timezone.setValue(data);
      this.form.controls.timezone.markAsDirty();
      this.form.controls.timezone.updateValueAndValidity();
    }
  }

  async submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      await this.service.toast("Bitte alle Felder korrekt ausfüllen.", "warning");
      return;
    }

    this.loading = true;
    const dto: CreateSiteDto = this.form.getRawValue() as CreateSiteDto;
    try {
      const result = await this.api.invoke(siteControllerCreate, {body: dto});

      const modal = await this.modalCtrl.create({
        component: SiteCreateSuccessComponent,
        componentProps: {token: result.apiToken},
      });

      await modal.present();
      await modal.onDidDismiss();

      await this.router.navigate(["/overview"]);
    } catch (e) {
      console.error(e);
      await this.service.toast("Fehler beim Erstellen des Standorts.", "danger");
    } finally {
      this.loading = false;
    }
  }
}
